/**
 * The ZIP, in the header, for a customer who arrived with one.
 *
 * The landing page's hero asks for the ZIP and checks it before sending the
 * customer here with `?zip=`, so asking again on step 1 would be asking twice.
 * Step 1 hides its field for them and this chip stands in for it: it says where
 * we are booking, and opens the same `ZipField` to change it.
 *
 * Never a dead end. A link can carry a ZIP we do not clean in (an old link, a
 * hand-typed one), so the chip turns red and opens itself with the refusal,
 * and step 1's Next opens it again rather than going on.
 */

import Popover from "antd/es/popover";
import { ChevronDown, MapPin } from "lucide-react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { setZip, setZipEditorOpen } from "src/actions";
import { useAreaCalendar } from "src/hooks/useAreaCalendar";
import { isZip } from "src/lib/proximity";
import { RootState } from "src";
import { ZipField } from "./ZipField";

const Chip = styled.button<{ $refused: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px 0 10px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$refused ? "red" : "#d6d9e3")};
  background: ${(p) => (p.$refused ? "#fdf1f0" : "white")};
  color: ${(p) => (p.$refused ? "red" : "#111827")};
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.06);

  svg {
    flex: 0 0 auto;
    color: ${(p) => (p.$refused ? "red" : "var(--blue-300)")};
  }

  &:hover,
  &:focus-visible {
    border-color: ${(p) => (p.$refused ? "red" : "var(--blue-300)")};
  }
`;

const Panel = styled.div`
  width: 280px;

  h2 {
    margin: 0 0 12px;
    font-size: 15px;
    font-weight: 700;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  font-size: 13px;
  color: #575e75;
`;

const Done = styled.button`
  height: 34px;
  padding: 0 16px;
  border: 0;
  border-radius: 6px;
  background: var(--blue-300);
  color: white;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    background: #aeb6da;
    cursor: not-allowed;
  }
`;

/** Hidden from sight, still read out. */
const Unseen = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export function ZipChip() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { zip, zipFromLink, zipEditorOpen, page } = useSelector(
    (state: RootState) => state.bookingReducer
  );
  const { calendar } = useAreaCalendar(zip);
  const refused = calendar?.status === "refused";

  /**
   * Steps 1 and 2: the ZIP decides coverage and the date step's cheaper days.
   * From step 3 the address is asked for in full and its ZIP wins.
   */
  const shown = zipFromLink && page <= 2 && pathname !== "/tasks";

  useEffect(() => {
    if (shown && refused) dispatch(setZipEditorOpen(true));
  }, [shown, refused, dispatch]);

  if (!shown) return null;

  const open = (value: boolean) => dispatch(setZipEditorOpen(value));
  /** Closed only on a ZIP we can use, or one we could not check. */
  const canClose = isZip(zip) && calendar !== null && !refused;

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={zipEditorOpen}
      onOpenChange={open}
      content={
        <Panel>
          <h2>Where should we clean?</h2>
          <ZipField
            zip={zip}
            calendar={calendar}
            // Not flagged while typing: Done stays off until the ZIP is whole.
            invalid={false}
            onChange={(value) => dispatch(setZip(value))}
            onEnter={() => canClose && open(false)}
          />
          <Footer>
            <span>
              Questions? <a href="sms:+19294974111">Text us</a>
            </span>
            <Done type="button" disabled={!canClose} onClick={() => open(false)}>
              Done
            </Done>
          </Footer>
        </Panel>
      }
    >
      <Chip type="button" $refused={refused} aria-expanded={zipEditorOpen}>
        <MapPin size={16} strokeWidth={2.25} aria-hidden="true" />
        {zip || "ZIP code"}
        <Unseen>{refused ? ", we don’t clean here yet. Change ZIP code" : ", change ZIP code"}</Unseen>
        <ChevronDown size={14} strokeWidth={2.25} aria-hidden="true" />
      </Chip>
    </Popover>
  );
}
